import React, { useMemo } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import {
  Activity,
  BarChart3,
  Tag,
  Image,
  Database,
  Square,
  Smartphone,
  Monitor,
  Search,
  Eye,
} from 'lucide-react'
import {
  StatsCard,
  StatsHeader,
  StatsTitle,
  StatsContent,
  StatsSection,
  SectionTitle,
  StatsGrid,
  CompactStatItem,
  CompactStatLabel,
  CompactStatValue,
  StatItem,
  StatLabel,
  StatValue,
  StatBadge,
  ScrollableContainer,
} from '../styles'

interface DisplacementStatsCardProps {
  isLoading?: boolean
  contentData?: any
  selectedNodes?: Set<string>
  visualizationMode?: string
  renderMode?: string
  tagIdToNameAndColor?: Record<string, { name: string; color: string }>
  onViewContent?: () => void
  outlierReasons?: Map<number, string[]>
}

const DisplacementStatsCard: React.FC<DisplacementStatsCardProps> = ({
  isLoading = false,
  contentData,
  selectedNodes = new Set(),
  visualizationMode = 'default',
  renderMode = 'full',
  tagIdToNameAndColor = {},
  onViewContent,
  outlierReasons,
}) => {
  // Get the actual selected content items
  const selectedContentItems = useMemo(() => {
    if (!contentData?.content || selectedNodes.size === 0) return []

    const contentArray = contentData.content
    const selectedItems: any[] = []

    selectedNodes.forEach((nodeId) => {
      const nodeIndex = parseInt(nodeId.replace('node-', ''))
      const contentItem = contentArray[nodeIndex]
      if (contentItem) {
        selectedItems.push(contentItem)
      }
    })

    return selectedItems
  }, [contentData, selectedNodes])

  const stats = useMemo(() => {
    if (selectedContentItems.length === 0) {
      return {
        hasSelection: false,
        totalImages: 0,
        annotatedImages: 0,
        unannotatedImages: 0,
        totalAnnotations: 0,
        tagBreakdown: {},
        dimensionCounts: {},
        unknownDimensions: 0,
        datasourceBreakdown: {},
        averageTagsPerImage: 0,
        averageAnnotationsPerImage: 0,
        uniqueTags: 0,
        uniqueDatasources: 0,
      }
    }

    const totalImages = selectedContentItems.length
    const annotatedImages = selectedContentItems.filter(
      (item: any) => item.annotation && item.annotation.length > 0
    ).length
    const unannotatedImages = totalImages - annotatedImages

    // Count total annotations and analyze tags
    let totalAnnotations = 0
    const tagCounts: Record<string, number> = {}
    const uniqueTagsSet = new Set<string>()

    selectedContentItems.forEach((item: any) => {
      if (item.annotation && Array.isArray(item.annotation)) {
        totalAnnotations += item.annotation.length
        item.annotation.forEach((ann: any) => {
          if (ann.tagids && Array.isArray(ann.tagids)) {
            ann.tagids.forEach((tagId: string) => {
              tagCounts[tagId] = (tagCounts[tagId] || 0) + 1
              uniqueTagsSet.add(tagId)
            })
          }
        })
      }
    })

    // Analyze actual image dimensions
    const dimensionCounts: Record<string, number> = {}
    let unknownDimensions = 0

    selectedContentItems.forEach((item: any) => {
      if (item.width && item.height) {
        const dimensionKey = `${item.width}×${item.height}`
        dimensionCounts[dimensionKey] = (dimensionCounts[dimensionKey] || 0) + 1
      } else {
        unknownDimensions++
      }
    })

    // Analyze datasources
    const datasourceCounts: Record<string, number> = {}
    selectedContentItems.forEach((item: any) => {
      if (item.metadata?.datasource) {
        datasourceCounts[item.metadata.datasource] =
          (datasourceCounts[item.metadata.datasource] || 0) + 1
      } else {
        datasourceCounts['Unknown'] = (datasourceCounts['Unknown'] || 0) + 1
      }
    })

    return {
      hasSelection: true,
      totalImages,
      annotatedImages,
      unannotatedImages,
      totalAnnotations,
      tagBreakdown: tagCounts,
      dimensionCounts,
      unknownDimensions,
      datasourceBreakdown: datasourceCounts,
      averageTagsPerImage:
        totalImages > 0
          ? Object.values(tagCounts).reduce((sum, count) => sum + count, 0) / totalImages
          : 0,
      averageAnnotationsPerImage: totalImages > 0 ? totalAnnotations / totalImages : 0,
      uniqueTags: uniqueTagsSet.size,
      uniqueDatasources: Object.keys(datasourceCounts).length,
    }
  }, [selectedContentItems])

  if (isLoading) {
    return (
      <StatsCard>
        <StatsHeader>
          <StatsTitle>
            <Activity size={16} />
            Selection Analysis
          </StatsTitle>
        </StatsHeader>
        <StatsContent>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
            }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        </StatsContent>
      </StatsCard>
    )
  }

  // Hide the card if no selection
  if (!stats.hasSelection) {
    return null
  }

  // Get tag names for display
  const getTagName = (tagId: string) => {
    return tagIdToNameAndColor[tagId]?.name || tagId
  }

  return (
    <StatsCard>
      <StatsHeader>
        <StatsTitle>
          <Activity size={16} />
          Selection Analysis
        </StatsTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <StatBadge variant="warning">{stats.totalImages} Images Selected</StatBadge>
          {onViewContent && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={onViewContent}
              style={{
                fontSize: '0.75rem',
                padding: '0.375rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
              }}
            >
              <Eye size={14} />
              View Content
            </Button>
          )}
        </div>
      </StatsHeader>
      <StatsContent style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        {/* Overview Section */}
        <StatsSection>
          <SectionTitle>
            <BarChart3 size={14} />
            Overview
          </SectionTitle>
          <StatsGrid>
            <CompactStatItem>
              <CompactStatLabel>Total Images</CompactStatLabel>
              <CompactStatValue>{stats.totalImages.toLocaleString()}</CompactStatValue>
            </CompactStatItem>
            <CompactStatItem>
              <CompactStatLabel>Total Annotations</CompactStatLabel>
              <CompactStatValue>{stats.totalAnnotations.toLocaleString()}</CompactStatValue>
            </CompactStatItem>
            <CompactStatItem>
              <CompactStatLabel>Avg Objects/Image</CompactStatLabel>
              <CompactStatValue>{stats.averageAnnotationsPerImage.toFixed(1)}</CompactStatValue>
            </CompactStatItem>
            <CompactStatItem>
              <CompactStatLabel>Avg Tags/Image</CompactStatLabel>
              <CompactStatValue>{stats.averageTagsPerImage.toFixed(1)}</CompactStatValue>
            </CompactStatItem>
          </StatsGrid>

          {/* Annotation Status */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                padding: '0.75rem',
                background: 'rgba(40, 167, 69, 0.1)',
                border: '1px solid rgba(40, 167, 69, 0.3)',
                borderRadius: '0.375rem',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#28a745',
                }}
              >
                {stats.annotatedImages.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '0.8rem',
                  color: '#adb5bd',
                  marginTop: '0.25rem',
                }}
              >
                Annotated (
                {stats.totalImages > 0
                  ? ((stats.annotatedImages / stats.totalImages) * 100).toFixed(1)
                  : 0}
                %)
              </div>
            </div>
            <div
              style={{
                padding: '0.75rem',
                background: 'rgba(108, 117, 125, 0.1)',
                border: '1px solid rgba(108, 117, 125, 0.3)',
                borderRadius: '0.375rem',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#6c757d',
                }}
              >
                {stats.unannotatedImages.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: '0.8rem',
                  color: '#adb5bd',
                  marginTop: '0.25rem',
                }}
              >
                Unannotated (
                {stats.totalImages > 0
                  ? ((stats.unannotatedImages / stats.totalImages) * 100).toFixed(1)
                  : 0}
                %)
              </div>
            </div>
          </div>
        </StatsSection>

        {/* Tag Analysis Section */}
        {stats.uniqueTags > 0 && (
          <StatsSection>
            <SectionTitle>
              <Tag size={14} />
              Tag Analysis ({stats.uniqueTags} unique tags)
            </SectionTitle>
            <ScrollableContainer>
              {Object.entries(stats.tagBreakdown)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .slice(0, 15) // Show top 15 tags
                .map(([tagId, count]) => {
                  const tagInfo = tagIdToNameAndColor[tagId]
                  const tagColor = tagInfo?.color || '#6c757d'

                  return (
                    <StatItem
                      key={tagId}
                      style={{
                        paddingTop: '0.5rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: tagColor,
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            flexShrink: 0,
                          }}
                        />
                        <StatLabel style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                          {getTagName(tagId)}
                        </StatLabel>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <StatValue style={{ fontSize: '0.9rem' }}>{count as number}</StatValue>
                        <span
                          style={{
                            display: 'inline-block',
                            backgroundColor: tagColor,
                            color: '#ffffff',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.65rem',
                            fontWeight: '500',
                            textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255,255,255,0.2)',
                          }}
                        >
                          {stats.totalImages > 0
                            ? (((count as number) / stats.totalImages) * 100).toFixed(1)
                            : 0}
                          %
                        </span>
                      </div>
                    </StatItem>
                  )
                })}
              {Object.keys(stats.tagBreakdown).length > 15 && (
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: '#adb5bd',
                    textAlign: 'center',
                    padding: '0.75rem 0',
                    fontStyle: 'italic',
                    background: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '0.25rem',
                    marginTop: '0.5rem',
                  }}
                >
                  +{Object.keys(stats.tagBreakdown).length - 15} more tags not shown
                </div>
              )}
            </ScrollableContainer>
          </StatsSection>
        )}

        {/* Image Dimensions Section */}
        <StatsSection>
          <SectionTitle>
            <Image size={14} />
            Image Dimensions ({Object.keys(stats.dimensionCounts).length} unique sizes)
          </SectionTitle>

          {/* Scrollable list of actual dimensions */}
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.4rem',
              }}
            >
              {Object.entries(stats.dimensionCounts)
                .sort(([, a], [, b]) => (b as number) - (a as number)) // Sort by count descending
                .map(([dimensions, count]) => {
                  const [width, height] = dimensions.split('×').map(Number)
                  const isSquare = width === height
                  const isPortrait = height > width
                  const isLandscape = width > height

                  // Color based on size category - neutral colors that don't imply quality
                  let color = '#6c757d'
                  let bgColor = 'rgba(108, 117, 125, 0.1)'
                  let borderColor = 'rgba(108, 117, 125, 0.3)'
                  const maxDim = Math.max(width, height)

                  if (maxDim >= 1024) {
                    color = '#6f42c1' // Purple for large images
                    bgColor = 'rgba(111, 66, 193, 0.1)'
                    borderColor = 'rgba(111, 66, 193, 0.3)'
                  } else if (maxDim >= 512) {
                    color = '#17a2b8' // Teal for medium-large images
                    bgColor = 'rgba(23, 162, 184, 0.1)'
                    borderColor = 'rgba(23, 162, 184, 0.3)'
                  } else if (maxDim >= 256) {
                    color = '#007bff' // Blue for medium images
                    bgColor = 'rgba(0, 123, 255, 0.1)'
                    borderColor = 'rgba(0, 123, 255, 0.3)'
                  } else if (maxDim >= 128) {
                    color = '#20c997' // Mint green for small images
                    bgColor = 'rgba(32, 201, 151, 0.1)'
                    borderColor = 'rgba(32, 201, 151, 0.3)'
                  } else {
                    color = '#fd7e14' // Orange for tiny images
                    bgColor = 'rgba(253, 126, 20, 0.1)'
                    borderColor = 'rgba(253, 126, 20, 0.3)'
                  }

                  return (
                    <div
                      key={dimensions}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.4rem 0.6rem',
                        background: bgColor,
                        border: `1px solid ${borderColor}`,
                        borderRadius: '0.25rem',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          color,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        {isSquare && <Square size={12} />}
                        {isPortrait && <Smartphone size={12} />}
                        {isLandscape && <Monitor size={12} />}
                        {dimensions}px
                      </span>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color,
                          }}
                        >
                          {count as number}
                        </span>
                        <span style={{ fontSize: '0.7rem', color, opacity: 0.8 }}>
                          {(((count as number) / stats.totalImages) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )
                })}

              {stats.unknownDimensions > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.4rem 0.6rem',
                    background: 'rgba(108, 117, 125, 0.1)',
                    border: '1px solid rgba(108, 117, 125, 0.3)',
                    borderRadius: '0.25rem',
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      color: '#6c757d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <Search size={12} />
                    Unknown Dimensions
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: '#6c757d',
                      }}
                    >
                      {stats.unknownDimensions}
                    </span>
                    <span
                      style={{
                        fontSize: '0.7rem',
                        color: '#6c757d',
                        opacity: 0.8,
                      }}
                    >
                      {((stats.unknownDimensions / stats.totalImages) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </StatsSection>

        {/* Data Sources Section */}
        {stats.uniqueDatasources > 0 && (
          <StatsSection>
            <SectionTitle>
              <Database size={14} />
              Data Sources ({stats.uniqueDatasources} sources)
            </SectionTitle>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {Object.entries(stats.datasourceBreakdown)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .map(([source, count]) => (
                  <div
                    key={source}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0.75rem',
                      background: 'rgba(17, 78, 224, 0.05)',
                      border: '1px solid rgba(17, 78, 224, 0.2)',
                      borderRadius: '0.25rem',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      {source === 'Unknown' ? (
                        <>
                          <Search size={12} />
                          Unknown
                        </>
                      ) : (
                        <>
                          <Database size={12} />
                          {source}
                        </>
                      )}
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: '#ffffff',
                        }}
                      >
                        {count as number}
                      </span>
                      <StatBadge variant="info" style={{ fontSize: '0.65rem' }}>
                        {(((count as number) / stats.totalImages) * 100).toFixed(1)}%
                      </StatBadge>
                    </div>
                  </div>
                ))}
            </div>
          </StatsSection>
        )}

        {/* Outlier Analysis Section - only show when outlier reasons are available */}
        {outlierReasons && selectedContentItems.length > 0 && (
          <StatsSection>
            <SectionTitle>
              <Activity size={14} />
              Outlier Analysis
            </SectionTitle>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
            >
              {selectedContentItems
                .map((item, itemIndex) => {
                  const contentIndex = contentData?.content?.findIndex((c: any) => c.id === item.id)
                  if (contentIndex === -1 || !outlierReasons.has(contentIndex)) return null

                  const reasons = outlierReasons.get(contentIndex) || []
                  return (
                    <div
                      key={item.id || itemIndex}
                      style={{
                        padding: '0.75rem',
                        background: 'rgba(220, 53, 69, 0.1)',
                        border: '1px solid rgba(220, 53, 69, 0.3)',
                        borderRadius: '0.375rem',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          color: '#dc3545',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        🚨 {item.name || `Image ${itemIndex + 1}`}
                      </div>
                      <div style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                        {reasons.map((reason, reasonIndex) => (
                          <div
                            key={reasonIndex}
                            style={{
                              color: '#ffffff',
                              marginBottom: '0.25rem',
                              paddingLeft: '0.5rem',
                              borderLeft: '2px solid rgba(220, 53, 69, 0.5)',
                            }}
                          >
                            • {reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })
                .filter(Boolean)}

              {selectedContentItems.filter((item, itemIndex) => {
                const contentIndex = contentData?.content?.findIndex((c: any) => c.id === item.id)
                return contentIndex !== -1 && outlierReasons.has(contentIndex)
              }).length === 0 && (
                <div
                  style={{
                    padding: '1rem',
                    background: 'rgba(40, 167, 69, 0.1)',
                    border: '1px solid rgba(40, 167, 69, 0.3)',
                    borderRadius: '0.375rem',
                    textAlign: 'center',
                    color: '#28a745',
                    fontSize: '0.85rem',
                  }}
                >
                  ✅ No outliers detected in selection
                </div>
              )}
            </div>
          </StatsSection>
        )}
      </StatsContent>
    </StatsCard>
  )
}

export default DisplacementStatsCard
